import { formatDate } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { AppBaseDataService } from 'src/app/_services/app-base-data.service';
import { FilterServiceService } from 'src/app/_services/filter-service.service';
import { GlobalService } from 'src/app/_services/global.service';
import { ProductsService } from 'src/app/_services/products.service';
import { StoreOverviewDateRange, StoreOverviewReportService } from 'src/app/_services/store-overview-report.service';

interface StoreOverviewPaymentRow {
  paymentMethod: string;
  amount: number;
  invoiceCount: number;
  share: number;
}

interface StoreOverviewProductRow {
  name: string;
  qty: number;
  cogs: number;
  margin: number | null;
}

@Component({
  selector: 'app-store-overview-report',
  templateUrl: './store-overview-report.component.html',
  styleUrls: ['./store-overview-report.component.scss']
})
export class StoreOverviewReportComponent implements OnInit, OnDestroy {
  _globals = GlobalService;

  readonly topProductsCount = 100;
  readonly barColors = ['#16a34a', '#3b82f6', '#d97706', '#8b5cf6', '#dc2626', '#0891b2'];

  data: any = null;
  errorMessage = '';
  paymentMethods: StoreOverviewPaymentRow[] = [];
  topProducts: StoreOverviewProductRow[] = [];

  constructor(
    private _service: StoreOverviewReportService,
    private _abd: AppBaseDataService,
    public _filterService: FilterServiceService,
    private _productService: ProductsService,
  ) { }

  ngOnInit(): void {
    this._globals.componentName = 'Store Overview';
    if (!this._filterService.dateRange || this._filterService.dateRange.length < 1) {
      this._filterService.initDates();
    }
    if (!this._filterService.selectedLocation) {
      this._filterService.selectedLocation = this._abd.locations;
    }
    // Category and Products pickers are hidden on this page, so don't keep filtering by a stale selection
    this._filterService.selectedCategory = '';
    this._filterService.selectedProducts = [];
    this.loadProductOptions();
    this.load();
  }

  /** Product picker options: one entry per Items.Id (duplicates from variants removed). */
  private loadProductOptions(): void {
    if (this._filterService.productOptions?.length) {
      return;
    }
    const pageSize = 100;
    const maxPages = 20;
    const collected: any[] = [];
    const fetchPage = (pageNo: number) => {
      this._productService.getAllProductList4InventoryV3(new HttpParams().set('pageNo', String(pageNo)).set('pageSize', String(pageSize))).subscribe({
        next: (res: any) => {
          const items: any[] = Array.isArray(res?.data) ? res.data : (res?.data?.items || []);
          if (!res?.success) {
            return;
          }
          collected.push(...items);
          const total = Number(res?.totalRecords) || 0;
          const hasMore = items.length === pageSize && (total ? collected.length < total : true);
          if (hasMore && pageNo + 1 < maxPages) {
            fetchPage(pageNo + 1);
            return;
          }
          const seen = new Set<string>();
          this._filterService.productOptions = collected
            .filter((p: any) => p?.id != null && !seen.has(String(p.id)) && !!seen.add(String(p.id)))
            .map((p: any) => ({ id: p.id, itemName: p.itemName }));
        },
        error: () => { /* leave options empty; reopening the page retries */ },
      });
    };
    fetchPage(0);
  }

  ngOnDestroy(): void {
    this._globals.componentName = '';
  }

  /** Top products paging (table + mobile cards share the same page size). */
  productRows = 10;
  readonly productRowOptions = [10, 25, 50, 100];
  cardsFirst = 0;

  get pagedProducts(): StoreOverviewProductRow[] {
    return this.topProducts.slice(this.cardsFirst, this.cardsFirst + this.productRows);
  }

  onCardsPage(event: any): void {
    this.cardsFirst = event?.first ?? 0;
    this.productRows = event?.rows ?? this.productRows;
  }

  /** Server-side product search from the Products picker; keeps already-selected products. */
  private productSearchTimer: any;
  searchProducts(text: string): void {
    clearTimeout(this.productSearchTimer);
    this.productSearchTimer = setTimeout(() => {
      const term = (text || '').trim();
      if (!term) {
        return;
      }
      this._productService.getAllProductList4InventoryV3(new HttpParams().set('pageNo', '0').set('pageSize', '50').set('filterValue', term)).subscribe({
        next: (res: any) => {
          if (!res?.success) {
            return;
          }
          const items: any[] = Array.isArray(res.data) ? res.data : (res.data?.items || []);
          const merged = new Map<string, any>();
          [...(this._filterService.productOptions || []), ...items.map((p: any) => ({ id: p.id, itemName: p.itemName }))]
            .forEach(o => { if (o?.id != null && !merged.has(String(o.id))) { merged.set(String(o.id), o); } });
          this._filterService.productOptions = Array.from(merged.values());
        },
      });
    }, 300);
  }

  get perf(): any {
    return this.data?.performance || {};
  }

  get selectedLocations(): any[] {
    const sel = this._filterService.selectedLocation;
    return Array.isArray(sel) ? sel : (sel ? [sel] : []);
  }

  get locationLabel(): string {
    const selected = this.selectedLocations;
    if (!selected.length || selected.length === (this._abd.locations || []).length) {
      return 'All Locations';
    }
    return selected.map((l: any) => l.locationName).join(', ');
  }

  filterButtonPressed(): void {
    this.load();
  }

  callbackFuncFromFilter(event?: any): void {
    this.load();
  }

  load(): void {
    const range = this._filterService.dateRange || [];
    if (range.length < 2 || !range[0] || !range[1]) {
      return;
    }

    const selected = this.selectedLocations;
    const allCount = (this._abd.locations || []).length;
    const body = {
      locationIds: selected.length && selected.length !== allCount ? selected.map((l: any) => String(l.locationId)) : [],
      dateRange: 'Custom' as StoreOverviewDateRange,
      startDate: this.toApiDate(range[0]),
      endDate: this.toApiDate(range[1]),
      categoryIds: this._filterService.selectedCategory?.id ? [String(this._filterService.selectedCategory.id)] : [],
      productIds: (this._filterService.selectedProducts || []).map((p: any) => String(p.id)),
      topProductsCount: this.topProductsCount,
      timeZone: null,
    };

    this.errorMessage = '';
    this._abd.showSpinner();
    this._service.getStoreOverview(body).subscribe({
      next: (res: any) => {
        this._abd.hideSpinner();
        if (res?.success) {
          this.data = res.data;
          this.buildPaymentRows();
          this.buildProductRows();
        } else {
          this.clearData();
          this.errorMessage = res?.message || 'Unable to load store overview.';
        }
      },
      error: () => {
        this._abd.hideSpinner();
        this.clearData();
        this.errorMessage = 'Unable to load store overview.';
      },
    });
  }

  formatMoney(value: any): string {
    return GlobalService.formatNumberWithCurrencySign(Number(value) || 0);
  }

  barColor(index: number): string {
    return this.barColors[index % this.barColors.length];
  }

  exportPdf(): void {
    if (!this.data) {
      return;
    }

    const doc = new jsPDF('p', 'pt', 'letter');
    const businessName = GlobalService.userInfo?.businessSetup?.[0]?.businessDisplayName || '';
    const start = this.data.period?.startDate ? formatDate(this.data.period.startDate, 'MM/dd/yyyy', 'en-US') : '';
    const end = this.data.period?.endDate ? formatDate(this.data.period.endDate, 'MM/dd/yyyy', 'en-US') : '';

    // Header: period and location top-left, business and report title centered (same as other report PDFs)
    doc.setFontSize(10);
    doc.text(['Start Date: ' + start, 'End Date: ' + end, 'Location: ' + this.locationLabel], 40, 30);
    doc.setFontSize(14);
    doc.text(businessName, 396, 90, { align: 'center' });
    doc.setFontSize(12);
    doc.text('Store Overview', 396, 108, { align: 'center' });

    let y = 130;
    const addSection = (title: string, head: any[][], body: any[][]) => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text(title, 40, y + 12);
      autoTable(doc, {
        startY: y + 20,
        head,
        body,
        margin: { left: 40, right: 40 },
        styles: { fontSize: 9 },
        headStyles: { fillColor: [55, 65, 81] },
      });
      y = (doc as any).lastAutoTable.finalY + 24;
    };

    const perf = this.perf;
    addSection(
      'Business Performance',
      [['Metric', 'Value']],
      [
        ['Total Sales', this.formatMoney(perf.totalSales)],
        ['Refunds', this.formatMoney(perf.refunds)],
        ['Net Sales', this.formatMoney(perf.netSales)],
        ['Total COGS (Sold)', this.formatMoney(perf.soldCogs)],
        ['Remaining Inventory Cost', this.formatMoney(perf.remainingInventoryCost)],
        ['Gross Profit', this.formatMoney(perf.grossProfit)],
        ['Gross Margin', `${Number(perf.grossMarginPercent || 0).toFixed(1)}%`],
      ]
    );

    if (this.paymentMethods.length) {
      addSection(
        'Sales by Payment Method',
        [['Method', 'Invoices', 'Amount', 'Share']],
        this.paymentMethods.map(p => [p.paymentMethod, String(p.invoiceCount), this.formatMoney(p.amount), `${p.share.toFixed(1)}%`])
      );
    }

    if (this.topProducts.length) {
      addSection(
        'Top Selling Products',
        [['Product', 'Qty Sold', 'Cost', 'Margin']],
        this.topProducts.map(p => [p.name, String(p.qty), this.formatMoney(p.cogs), p.margin == null ? '-' : `${p.margin}%`])
      );
    }

    doc.save('Store Overview (' + formatDate(new Date(), 'yyyy-MM-dd', 'en-US') + ').pdf');
  }

  exportExcel(): void {
    if (!this.data) {
      return;
    }

    const businessName = GlobalService.userInfo?.businessSetup?.[0]?.businessDisplayName || '';
    const start = this.data.period?.startDate ? formatDate(this.data.period.startDate, 'MM/dd/yyyy', 'en-US') : '';
    const end = this.data.period?.endDate ? formatDate(this.data.period.endDate, 'MM/dd/yyyy', 'en-US') : '';
    const perf = this.perf;

    // Same content and section order as the PDF, stacked on one sheet
    const rows: any[][] = [
      ['Start Date', start],
      ['End Date', end],
      ['Location', this.locationLabel],
      [],
      [businessName],
      ['Store Overview'],
      [],
      ['Business Performance'],
      ['Metric', 'Value'],
      ['Total Sales', this.formatMoney(perf.totalSales)],
      ['Refunds', this.formatMoney(perf.refunds)],
      ['Net Sales', this.formatMoney(perf.netSales)],
      ['Total COGS (Sold)', this.formatMoney(perf.soldCogs)],
      ['Remaining Inventory Cost', this.formatMoney(perf.remainingInventoryCost)],
      ['Gross Profit', this.formatMoney(perf.grossProfit)],
      ['Gross Margin (%)', Number(perf.grossMarginPercent) || 0],
    ];

    if (this.paymentMethods.length) {
      rows.push([], ['Sales by Payment Method'], ['Method', 'Invoices', 'Amount', 'Share (%)']);
      this.paymentMethods.forEach(p => rows.push([p.paymentMethod, p.invoiceCount, this.formatMoney(p.amount), Number(p.share.toFixed(1))]));
    }

    if (this.topProducts.length) {
      rows.push([], ['Top Selling Products'], ['Product', 'Qty Sold', 'Cost', 'Margin (%)']);
      this.topProducts.forEach(p => rows.push([p.name, p.qty, this.formatMoney(p.cogs), p.margin == null ? '' : Number(p.margin)]));
    }

    import('xlsx').then(xlsx => {
      const sheet = xlsx.utils.aoa_to_sheet(rows);
      sheet['!cols'] = [{ wch: 28 }, { wch: 16 }, { wch: 16 }, { wch: 14 }];
      const workbook = { Sheets: { 'Store Overview': sheet }, SheetNames: ['Store Overview'] };
      const buffer: any = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
      this._abd.saveAsExcelFile(buffer, 'Store Overview');
    });
  }

  private buildPaymentRows(): void {
    const rows = (this.data?.paymentMethods || []).filter((p: any) => Number(p.amount) > 0 || Number(p.invoiceCount) > 0);
    const total = rows.reduce((sum: number, p: any) => sum + (Number(p.amount) || 0), 0);
    this.paymentMethods = rows.map((p: any) => ({
      paymentMethod: p.paymentMethod,
      amount: Number(p.amount) || 0,
      invoiceCount: Number(p.invoiceCount) || 0,
      share: total > 0 ? ((Number(p.amount) || 0) / total) * 100 : 0,
    }));
  }

  private buildProductRows(): void {
    // Top-product field names are not in the sample response (topProducts was empty); read with fallbacks.
    this.topProducts = (this.data?.topProducts || []).map((p: any) => ({
      name: p.productName ?? p.name ?? p.itemName ?? '-',
      qty: Number(p.qtySold ?? p.quantitySold ?? p.quantity ?? 0),
      cogs: Number(p.soldCogs ?? p.cogs ?? p.cost ?? 0),
      margin: p.grossMarginPercent ?? p.marginPercent ?? p.margin ?? null,
    }));
  }

  private clearData(): void {
    this.data = null;
    this.paymentMethods = [];
    this.topProducts = [];
  }

  private toApiDate(d: Date): string {
    return formatDate(d, 'yyyy-MM-dd', 'en-US');
  }
}
